
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Dialog, DialogHeader, Row, Col } from '../bootstrap/index.jsx';
import TextInput from '../common/TextInput.jsx';
import Select from '../common/Select.jsx';
import { Portal } from "../common/Portal.jsx";
import ToggleSwitch from '../common/ToggleSwitch.jsx';
import { fetchJson } from '../../helpers/fetch.js';
import { update } from '../../helpers/index.js';


import Backend from "react-dnd-html5-backend";
import { DragHandle, DropArea } from "../common/DragNDrop.jsx";
import { DndProvider } from "react-dnd";

import useDynamicCols, { buildColumnDefs } from './hooks/useDynamicCols.jsx';

import Errors from '../common/Errors.jsx'
import FormulaEditor from './formula_editor/FormulaEditor.lazy.jsx';
import PercentageOverInput from './PercentageOverInput.jsx';
import ComputedMatchColumnType from './ColumnEditComponent/ComputedMatchColumnType.jsx';

import {
  datagrid_api_client_endpoint_list_json_path,
  datagrid_schema_options_for_select_path,
  datagrid_update_path,
  datagrid_delete_col_path,
  programme_form_option_library_ag_grid_v_path,
  datagrid_test_formula_path,
  option_set_list_json_path,
  option_set_hierarchy_list_json_path
} from '~/routes';

import AgGridReact from '../common/AgGrid/AgGridReact.jsx';
import { gridTheme } from '../common/AgGrid/gridTheme.js';
import CommonSlider from '../common/CommonSlider.jsx';
import CommonColorPicker from '../common/ColorPicker/CommonColorPicker.jsx';

const DND_ITEM_TYPE = "selection-option";

const PRE_DEFINED_VALUE_FORMATTERS = [
  'positiveValueFormatter',
  'negativeValueFormatter'
]

// Indicates how the value should be stored in the database
const PRE_DEFINED_VALUE_CONSTRAINTS = {
  'positiveValueFormatter': ['positive', 'negative'],
  'negativeValueFormatter': ['positive', 'negative']
}

export const DialogFooter = ({
  onClose, 
  onSubmit,
  closeText,
  submitText,
  disabledText,
  disabled = false,
  handleDeleteCol,
  deletable,
  isCreate = false
}) =>  {
  const [keepOpen, setKeepOpen] = useState(false);

  const handleAdd = (e) => {
    onSubmit(e, keepOpen)
  }

  return <div className="modal-footer">
    <div style={{display: 'flex', justifyContent: 'space-between'}}>
      <button disabled={disabled} className="btn btn-default" onClick={onClose}>{closeText || I18n.t('close')}</button>
      <div style={{display: 'grid', gap: '1rem', marginRight: '1rem', alignItems: 'center', gridAutoFlow: 'column'}}>
        {!isCreate ? (
          deletable && (<button disabled={disabled} className="btn btn-danger" onClick={handleDeleteCol}>
            <span className="icon-trash"></span>
          </button>)
        ) : (
          <>
            <input id='add_another' type='checkbox' checked={keepOpen} onChange={() => setKeepOpen(prev => !prev)}/>
            <label htmlFor='add_another' className='description'>{I18n.t('add_another')}</label>
          </>
        )}
        <button disabled={disabled} className="btn btn-primary" onClick={handleAdd}>{disabled && disabledText ? disabledText : submitText || I18n.t('add')}</button>
      </div> 
    </div>
  </div>
}

// Show component only if columnType listed under showOnColumnTypes and not listed under hideOndColumnTypes
const ColumnTypeDependent = ({children, columnType, showOnColumnTypes = null, hideOnColumnTypes = []}) => {
  if (showOnColumnTypes && !showOnColumnTypes.includes(columnType)){
    return null;
  }
  if (hideOnColumnTypes.includes(columnType)) {
    return null;
  }
  return children;
}

const TestFormulaGrid = ({ columnDefs, formula, accessor, context }) => {
  const [rowData, setRowData] = useState([{}]);

  // Value Change Detection - When a value for any cell changes
  const onCellEdited = useCallback((event) => {
    fetchJson(
      datagrid_test_formula_path({id: context.id, context_name: context.context_name, _options: true}), {
        method: 'POST',
        body: JSON.stringify({formula: formula, variables: event.data})
      }
    ).then(res => {
      if (res.valid) {
        setRowData([{...event.data, [accessor]: res.value, formula_errors: {}}]);
      } else {
        setRowData([{...event.data, [accessor]: '', formula_errors: {[accessor]: res.error}}]);
      }
    })
  });

  return (
    <div className='grid-theme' style={{ height: 157, width: '100%' }}>
      <AgGridReact theme={gridTheme}
        columnDefs={columnDefs.map(v => ({...v, editable: _.isEmpty(v.cellRendererParams?.original?.formula)}))}
        rowData={rowData}
        defaultColDef={{
          editable: true,
          filter: false,
          resizable: true,
          autoHeight: true
        }}
        onCellValueChanged={onCellEdited}
        stopEditingWhenCellsLoseFocus
      />
    </div>
  )
};

export const ColumnEdit = ({defaultValues, columns, onClose, title, formOptions, api, context, isCreate}) => {

  const [values, setValues] = useState(defaultValues)
  const [selectionOptions, setSelectionOptions] = useState(defaultValues.selection_options || []);
  const [dependencySettings, setDependencySettings] = useState(defaultValues.dependency_settings || {});

  const [saving, setSaving] = useState(false);
  const [rowCount, setRowCount] = useState(null);
  const [errors, setErrors] = useState({})

  const [apiClients, setApiClients] = useState([])

  const [datagridSchemaOptions, setDatagridSchemaOptions] = useState([]);
  const [optionSets, setOptionSets] = useState([]);
  const [optionSetHierarchies, setOptionSetHierarchies] = useState([]);

  const [testFormulaTooltip , setTestFormulaTooltip] = useState(false);

  const datagridOptions = useMemo(() => {
    return datagridSchemaOptions.map(v => ({value: v.value, label: v.label}));
  }, [datagridSchemaOptions]);

  const riskTypeOptions = useMemo(() => {
    return ['risk_severity', 'risk_likelihood'].map(v => ({value: v, label: I18n.t(v)}))
  }, []);

  const datagridColOptions = useMemo(() => {
    return datagridSchemaOptions.find(v => v.value == values.targeted_datagrid_id)?.cols || [];
  }, [datagridSchemaOptions, values.targeted_datagrid_id]);

  const handleValueChange = (field, value, isSettingAttribute = false) => {
    if (field === 'use_option_set_hierarchy' && value) {
      setValues(prev => ({
        ...prev,
        [field]: value,
        option_set_hierarchy_level: prev.option_set_hierarchy_level ?? 1,
      }));
      return;
    }
    if(field === 'is_selection'){
      if(selectionOptions.length === 0 && value === true){
        handleAddOption();
      }
    } else if (['name', 'key'].includes(field)) {
      // Replace brackets with an empty string
      value = value.replace(/[\[\]]/g, '');
    }
    if (isSettingAttribute) {
      setValues((prev) => ({
        ...prev, additional_settings: update(prev.additional_settings || {}, {$merge: {[field]: value}}),
      }))
    } else {
      setValues(prev => ({...prev, [field]: value}));
    }
  };

  const handleAddOption = () => {
    setSelectionOptions((preVal) => [...preVal, {label: `Option ${preVal.length+1}`, value: `${preVal.length+1}`}])
  };

  const openOptionLibrary = (event) => {
    let callback = window; // Root for the lookup
    context.optionLibraryAgGridVCallback.split('.').forEach((i) => {
        callback = callback[i];
    });

    callback('datagrid-selection-options', '_default', 'en');

    event.preventDefault();
  }

  const handleAddOptionFromLibrary = (event) => {
    const formLibraryButton = jQuery('#add-options-from-library');
    let newOptions = JSON.parse(formLibraryButton.attr('newoptions'))
    let skipDuplicates = JSON.parse(formLibraryButton.attr('data-skip-duplicates'))

    if (skipDuplicates) {
        newOptions = newOptions.filter(o => !selectionOptions.find(opt => o.value == opt.value))
    }

    // Only support 'en' because datagrid options doesn't specify language
    setSelectionOptions((preVal) => [...preVal, ...newOptions.map(op => {return {label: op.label.en, value: op.value}})])
  }

  const handleUpdateOption = (idx, field, value) => {
    let item = selectionOptions[idx];
    item[field] = value;
    setSelectionOptions(Object.values({...selectionOptions, [idx]: item}))
  };

  const handleRemoveOption = (idx) => {
    selectionOptions.splice(idx, 1);
    setSelectionOptions(() => [...selectionOptions])
  };

  useEffect(() => {
     handleValueChange('selection_options', selectionOptions)
  }, [selectionOptions]);

  useEffect(() => {
    setRowCount(api.getDisplayedRowCount());
  }, []);

  useEffect(() => {
    if (isCreate || values.column_type === 'option_set') {
      jQuery.getJSON(option_set_list_json_path(), (data) => {
        setOptionSets(data.option_sets);
      });

      jQuery.getJSON(option_set_hierarchy_list_json_path(), (data) => {
        setOptionSetHierarchies(data.hierarchies);
      });
    }

    if (isCreate || values.column_type === 'text') {
      jQuery.getJSON(datagrid_api_client_endpoint_list_json_path({id: context.id, context_name: context.context_name, _options: true}), (data) => {
        setApiClients(data.clients);
      });
    }

    // Loads schema options of the data-grid reference
    if (isCreate || values.column_type === 'datagrid_reference')
      jQuery.getJSON(datagrid_schema_options_for_select_path({id: context.id, context_name: context.context_name, _options: true}), (data) => {
        setDatagridSchemaOptions(data);
      });
  }, []);

  const handleNameChange = (e) => handleValueChange('name', e.target.value);
  const handleFormulaChange = (value) => handleValueChange('formula', value);

  const handleSave = (_e, keepOpen) => {
    setSaving(true);
    fetchJson(datagrid_update_path({id: context.id, context_name: context.context_name, _options: true}), {body: JSON.stringify({definition: values}), method: 'POST'}).then(columns => {
      if (columns.errors){
        setErrors(columns.errors)
        setSaving(false);
      } else {
        setErrors({})
        const newColumnDefs = buildColumnDefs(columns, {supressBulkActions: true});
        api.setGridOption('columnDefs', newColumnDefs);  
        setSaving(false);
        if (!keepOpen) {
          onClose();
        } else {
          // Reset all form state when keeping the dialog open for "add another"
          setValues({})
          setSelectionOptions([])
          setDependencySettings({})
          setErrors({})
        }
      }})
    }

  const handleDeleteCol = (e, id) => {
    if (confirm(I18n.t('confirm_delete_datagrid_column',{name: values.name}))) {
      fetchJson(datagrid_delete_col_path({ id: context.id, context_name: context.context_name, _options: true }), {body: JSON.stringify({column_id: id}), method: 'POST'}).then(res=>{
        if (res.errors) {
          alert(validationErrorMessagesAsList(res.errors).join('\n'))
        } else {
          const newColumnDefs = buildColumnDefs(res, {supressBulkActions: true});
          api.setGridOption('columnDefs', newColumnDefs);
          onClose();
        }})
    }
  };

  const reorderOption = (dragIndex, hoverIndex) => {
    const selections = [...selectionOptions];
    const [draggedItem] = selections.splice(dragIndex, 1);
  
    selections.splice(hoverIndex, 0, draggedItem);
    setSelectionOptions(() => [...selections])
  };
  

  const dataTypeOptions = useMemo(() => {  
    let types = [...formOptions.dataTypes];

    if (isCreate) {
      let systemColumnTypes = ['custom_sql', 'connected_users'];
      let columnTypes = columns.map(c => c.cellRendererParams?.original?.column_type);
      return types.map(t =>
        ({...t, options: t.options.filter(v => !systemColumnTypes.includes(v.value)).map(o => ({...o, disabled: o.label_props?.static && columnTypes.find(t => t == o.value)}))})
      );
    }
    if (rowCount == null) return []; // Row count not loaded
    if (rowCount == 0) return types; // No rows, no limitations
    
    // Disable if isn't suitable & some data already exists
    return types.map(t => (
      {
        ...t,
        options: t.options.map(o => ({
          ...o,
          disabled: ![
            ...(formOptions.dataTypeConversions[values.column_type] || []), 
            values.column_type
          ].includes(o.value)
        }))
      }
    ))

  }, [formOptions.dataTypes, rowCount, values, isCreate]);

  const clientOptions = useMemo(() => {
    return apiClients.map(ac => ({
      value: ac.id,
      label: ac.object_type
    }))
  }, [apiClients])

  const attrOptions = useMemo(() => {
    return Object.keys(apiClients.find(a => a.id == values.selection_api_client_endpoint)?.metadata ?? {}).map(k => ({
      value: k,
      label: k
    }))
  }, [apiClients, values])

  const handleTestFormulaTooltip = () => {
    setTestFormulaTooltip(o => !o);
  };

  const handleDependencySettingsChange = (attr, val) => {
    let updatedSettings = update(dependencySettings, {[attr]: {$set: val}});

    if(attr == 'accessor') {
      updatedSettings = {...updatedSettings, mapping: []}
    }

    setDependencySettings(updatedSettings);
    setValues(prev => ({...prev, ['dependency_settings']: updatedSettings}));
  };

  const handleDependencyMappingChange = (mappedValue, valueConstraint) => {
    let updatedSettings = update(dependencySettings, {
      'mapping': (obj) => {
        const currentMapping = obj || {};
        return {...currentMapping, [`option-${mappedValue}`]: {'value_constraint': valueConstraint}};
      }
    });

    setDependencySettings(updatedSettings);
    setValues(prev => ({...prev, ['dependency_settings']: updatedSettings}));
  };

  const targetedDependencyOptions = useMemo(() => {
    if (dependencySettings['no_mapping_defined']) return []
    return columns.map(v => v.cellRendererParams?.original).find(v => v?.accessor == dependencySettings['accessor'])?.selection_options || []
  }, [dependencySettings])

  useEffect(() => {
    setTestFormulaTooltip(false);
  }, [values.formula]);

  const useOptionSetHierarchy = values.use_option_set_hierarchy;
  const optionSetSelectConfig = useMemo(() => {
    const selectedKeyName = useOptionSetHierarchy ? 'option_set_hierarchy_key' : 'option_set_key';
    const clearedKeyName = useOptionSetHierarchy ? 'option_set_key' : 'option_set_hierarchy_key';
    const translationKey = useOptionSetHierarchy ? 'option_set_hierarchy' : 'option_set';
    const availableOptionSets = useOptionSetHierarchy ? optionSetHierarchies : optionSets;

    const selectOptions = availableOptionSets.map((optionSet) => ({ value: optionSet.key, label: optionSet.label }));

    return {
      selectedKeyName,
      clearedKeyName,
      translationKey,
      selectOptions,
    };
  }, [useOptionSetHierarchy, optionSetHierarchies, optionSets]);

  return <Dialog 
    header={<DialogHeader onClose={onClose} title={title} showBorder={false} />} 
    footer={
      <DialogFooter 
        deletable={values.deletable} 
        onClose={onClose} 
        onSubmit={handleSave} 
        disabled={saving} 
        submitText={isCreate ? I18n.t('add') : I18n.t('save')} 
        handleDeleteCol={(e) => handleDeleteCol(e, values.id)} 
        isCreate={isCreate}
      />
    }
    size='md'
    closeOnOutsideClick={false}
    onClose={onClose}
    style={{zIndex: 1049}} // Make sure this dialog below the select from library dialog
  >
      {errors ? <Errors errors={errors} /> : null}

      <Row>
        <Col>
          <label>{I18n.t('type')} *</label>
          <Select 
            defaultValue={values.column_type} 
            onChange={(e) => handleValueChange('column_type', e)} 
            options={dataTypeOptions}
            disabled={dataTypeOptions.every(o => o.disabled)}
          />
        </Col>
        <Col className='mt-1'>
          <label>{I18n.t('name')} *</label>
          <TextInput value={values.name} onChange={handleNameChange}/>
        </Col>
        <Col className='mt-1'>
          <label>{I18n.t('datagrid_column_description')}</label>
          <TextInput value={values.description} maxLength='200' onChange={(e) => handleValueChange('description', e.target.value)}/>
        </Col>
        <Col className='mt-1'>
          <label>{I18n.t('header_group')}</label>
          <TextInput value={values.group} onChange={(e) => handleValueChange('group', e.target.value)}/>
        </Col>
  
        <ColumnTypeDependent key='unit' columnType={values.column_type} showOnColumnTypes={['integer', 'decimal']}>
          <Col className='mt-1'>
            <label>{I18n.t('unit')}</label>
            <Select defaultValue={values.unit} onChange={(e) => handleValueChange('unit', e)} options={formOptions.units}/>
          </Col>
        </ColumnTypeDependent>
          <ColumnTypeDependent key='decimal_places' columnType={values.column_type} showOnColumnTypes={['decimal', 'sparkline']}>
          <Col className='mt-1'>
            <label>{I18n.t('decimal_places')}</label>
            <div>
              <CommonSlider value={values.decimal_places} min={0} max={10} step={1} onChange={(v) => handleValueChange('decimal_places', v)} style={{width: '100%'}}/>
              <p className='description'>{I18n.t('decimal_places_description')}</p>
            </div>
          </Col>
        </ColumnTypeDependent>
        <ColumnTypeDependent key='formula' columnType={values.column_type} showOnColumnTypes={['integer', 'decimal', 'text', 'date', 'sparkline', 'change_tracking']}>
          <Col className='mt-1'>
            <label className='w-100'>{I18n.t('formula')}</label>
            <ToggleSwitch 
              isOn={values.formula != null} 
              defaultIsOn={false} 
              // Only can be toggled off if formula is cleared to avoid accidentally losing formula
              handleToggle={(e) => {handleFormulaChange(values.formula == '' ? null : values.formula || '')}} 
              disabled={!(values.formula == '' || !values.formula)}
              name='formula'
            />
            {values.formula != null &&
              <>
                <FormulaEditor
                  columns={columns.map(v =>({value: v.field, label: v.headerName, key: v?.cellRendererParams?.original?.key}))}
                  defaultValue={values.formula}
                  onChange={handleFormulaChange}
                  dispalyInstructions={true}
                />

                {!_.isEmpty(values.formula) &&
                  <div>
                    { testFormulaTooltip ?
                      <>
                        <a onClick={handleTestFormulaTooltip} className='btn btn-link pl-0'>{I18n.t('hide_test_formula')}</a>
                        <div>
                          <TestFormulaGrid
                            columnDefs={[...api.getColumnDefs(), isCreate ? useDynamicCols([{accessor: 'test', column_type: values.column_type, name: values.name, formula: values.formula, display_format: true}])[1] : false].filter(c => c)}
                            formula={values.formula}
                            accessor={values.accessor || 'test'}
                            context={context}
                          />
                        </div>
                      </>
                      :
                      <a onClick={handleTestFormulaTooltip} className='btn btn-link pl-0'>{I18n.t('display_test_formula')}</a>
                    }
                  </div>
                }
              </>
            }
          </Col>
        </ColumnTypeDependent>
        <ColumnTypeDependent columnType={values.column_type} showOnColumnTypes={['option_set']}>
          <Col className='mt-1'>
            <label>{I18n.t(optionSetSelectConfig.translationKey)}</label>
            <Select
              key={optionSetSelectConfig.selectedKeyName}
              value={values[optionSetSelectConfig.selectedKeyName]}
              onChange={(selectedOption) => {
                handleValueChange(optionSetSelectConfig.clearedKeyName, null);
                handleValueChange(optionSetSelectConfig.selectedKeyName, selectedOption);
              }}
              options={optionSetSelectConfig.selectOptions}
            />
          </Col>
          <Col className='mt-1' size='xs-4'>
            <label>{I18n.t('use_hierarchy')}</label>
            <div>
              <ToggleSwitch name='use_option_set_hierarchy' isOn={values.use_option_set_hierarchy} defaultIsOn={false} handleToggle={(e) => handleValueChange('use_option_set_hierarchy', !values.use_option_set_hierarchy)} />
            </div>
          </Col>
          {!useOptionSetHierarchy && <Col className='mt-1' size='xs-4'>
            <label>{I18n.t('is_array')}</label>
            <div>
              <ToggleSwitch name='is_array' isOn={values.is_array} defaultIsOn={false} handleToggle={(e) => handleValueChange('is_array', !values.is_array)} />
            </div>
          </Col>}
          {useOptionSetHierarchy && <Col className='mt-1'>
            <label>{I18n.t('level')}</label>
            <div>
              <CommonSlider value={values.option_set_hierarchy_level ?? 1} min={1} max={10} step={1} onChange={(v) => handleValueChange('option_set_hierarchy_level', v)} style={{width: '100%'}}/>
              <p className='description'>{I18n.t('option_set_hierarchy_level_description', {scope: 'fe'})}</p>
            </div>
          </Col>}
        </ColumnTypeDependent>
        <ColumnTypeDependent columnType={values.column_type} showOnColumnTypes={['text', 'sparkline']}>
          <Col className='mt-1' size='xs-4'>
            <label>{I18n.t('is_selection')}</label>
            <div>
              <ToggleSwitch name='is_selection' isOn={values.is_selection} defaultIsOn={false} handleToggle={(e) => handleValueChange('is_selection', !values.is_selection)} />
            </div>
          </Col>
          {values.is_selection && 
          <>
            {values.column_type !== 'sparkline' && <>
            <Col className='mt-1' size='xs-4'>
              <label>{I18n.t('is_array')}</label>
              <div>
                <ToggleSwitch name='is_array' isOn={values.is_array} defaultIsOn={false} handleToggle={(e) => handleValueChange('is_array', !values.is_array)} />
              </div>
            </Col>
            <Col className='mt-1' size='xs-4'>
              <label>{I18n.t('is_external_entity')}</label>
              <div>  
                <ToggleSwitch name='is_external_entity' isOn={values.is_external_entity} defaultIsOn={false} handleToggle={(e) => handleValueChange('is_external_entity', !values.is_external_entity)} />
              </div>
            </Col></>}
            <Col className='mt-1' size='xs-4'>
              <label>{I18n.t('has_background_color')}</label>
              <div>  
                <ToggleSwitch name='has_background_color' isOn={values.has_background_color} defaultIsOn={false} handleToggle={(e) => handleValueChange('has_background_color', !values.has_background_color)} />
              </div>
            </Col>

            {/* TODO: <Col className='mt-1'>
              <label>{I18n.t('creatable_options')}</label>
              <ToggleSwitch name='creatable_options' isOn={values.is_creatable} defaultIsOn={false} handleToggle={(e) => handleValueChange('is_creatable', !values.is_creatable)} />
            </Col> */}
            {values.is_external_entity ? <Col>
              <Col className='mt-1 pl-0' size='xs-4'>
                <label>{I18n.t('api_client_endpoint')}</label>
                <Select defaultValue={values.selection_api_client_endpoint} onChange={(e) => handleValueChange('selection_api_client_endpoint', e)} options={clientOptions}/>
              </Col>
              <Col className='mt-1' size='xs-4'>
                <label>{I18n.t('field_for_value')}</label>
                <Select defaultValue={values.selection_value_field} onChange={(e) => handleValueChange('selection_value_field', e)} options={attrOptions}/>
              </Col>
              <Col className='mt-1' size='xs-4'>
                <label>{I18n.t('field_for_label')}</label>
                <Select defaultValue={values.selection_label_field} onChange={(e) => handleValueChange('selection_label_field', e)} options={attrOptions}/>
              </Col>
            </Col> 
            :
            <DndProvider backend={Backend}>
            <Col className='mt-1 dragHandle'>
              <label>{I18n.t('options')}</label>
              <div style={{display: 'flex'}}>
                <div className="option-value ml-1" style={{flex: 2, paddingLeft: "16px"}}>
                    <label>{I18n.t("value")}</label> 
                </div>
                <div className="option-label mr-1" style={{flex: 3}}>
                    <label>{I18n.t("label")}</label> 
                </div>
                {values.has_background_color && 
                  <div className="option-color mr-1" style={{flex: 3}}>
                    <label>{I18n.t("color")}</label> 
                  </div>}

              </div>
              {selectionOptions.map(({label, value, color}, idx) => (
                <DropArea
                  key={`row-${idx}`}
                  accept={DND_ITEM_TYPE}
                  item={{ id: `row-${idx}`, index: idx, type: DND_ITEM_TYPE }}
                  onMove={reorderOption}
                  render={({ dropRef, dragRef, isDragging }) => (
            
                    <div ref={dropRef}>
                      <div className='mb-1' style={{display: 'flex'}}>
                        <DragHandle dragRef={dragRef} />
                        <div className="option-value ml-1" style={{flex: 2}}>
                            <input 
                              type="text" 
                              value={value} 
                              onChange={(e) => handleUpdateOption(idx, 'value', e.target.value)}
                              placeholder={I18n.t('option_value', {scope: 'fe'})} 
                              className="form-control" 
                            />
                        </div>
                        <div className="option-label ml-1" style={{flex: 3}}>
                            <input 
                              type="text" 
                              value={label} 
                              onChange={(e) => handleUpdateOption(idx, 'label', e.target.value)}
                              placeholder={I18n.t('option_label', {scope: 'fe'})} 
                              className="form-control" 
                            />
                        </div>
                          {values.has_background_color && <div className="option-color ml-1" style={{flex: 3}}>
                            <CommonColorPicker
                              value={color}
                              onChange={(value) => handleUpdateOption(idx, 'color', value)}
                              placeholder={I18n.t('option_color', {scope: 'fe'})} 
                            />
                          </div>}
                        <a 
                          href="#" 
                          className="icon-trash text-danger ml-1"
                          style={{
                            alignSelf: 'center',
                            fontSize: '18px'
                          }} 
                          title={I18n.t('delete_option', {scope: 'fe'})} 
                          onClick={(e) => { e.preventDefault(); handleRemoveOption(idx)}}></a>
                      </div>
                    </div>
              )}
              />
              ))}
              <div className="add-buttons-datagrid-config-columns">
                <a href="#" className="btn btn-primary btn-sm" onClick={handleAddOption}><i className="icon-add"></i> {I18n.t('add_option', {scope: 'fe'})}</a>
                <a href="#" className="btn btn-primary btn-sm" onClick={openOptionLibrary}><i className="icon-add"></i> {I18n.t('add_from_library', {scope: 'fe'})}...</a>
              </div>
              <script type="text/template" id="form-editor-option-library-ag-grid-v-url">
                {programme_form_option_library_ag_grid_v_path({programme_id: context.programme_id})}
              </script>
              <button type='button' id='add-options-from-library' style={{display: 'none'}} onClick={handleAddOptionFromLibrary} newoptions='{}'></button>
            </Col>
          </DndProvider>
          }
        </>
        }
        </ColumnTypeDependent>

        <ColumnTypeDependent columnType={values.column_type} showOnColumnTypes={['percentage_over']}>
          <PercentageOverInput 
            defaultValue={values.select_parts} 
            onChange={(newValue) => handleValueChange('select_parts', newValue)} 
            columns={columns}
          />
        </ColumnTypeDependent>

        <ColumnTypeDependent columnType={values.column_type} showOnColumnTypes={['datagrid_reference']}>
          <Col className='mt-1'>
            <label>{I18n.t('datagrid')}</label>
            <Select defaultValue={values.targeted_datagrid_id} onChange={(e) => handleValueChange('targeted_datagrid_id', e)} options={datagridOptions}/>
          </Col>
          <Col className='mt-1'>
            <label>{I18n.t('column')}</label>
            <Select defaultValue={values.targeted_datagrid_col_accessor} onChange={(e) => handleValueChange('targeted_datagrid_col_accessor', e)} options={datagridColOptions}/>
          </Col>
        </ColumnTypeDependent>

        <ColumnTypeDependent columnType={values.column_type} showOnColumnTypes={['computed_match']}>
          <ComputedMatchColumnType {...{values: values.computed_match_settings || {}, accessor: values.accessor, columns, setValues}}/>
        </ColumnTypeDependent>

        <ColumnTypeDependent columnType={values.column_type} showOnColumnTypes={['integer', 'decimal', 'option_set']}>
          {(values.column_type !== 'option_set' || useOptionSetHierarchy) && <Col className='mt-1'>
            <label className='w-100'>{I18n.t('has_dependency')}</label>
            <ToggleSwitch name='has_dependency' isOn={values.has_dependency} defaultIsOn={false} handleToggle={(e) => handleValueChange('has_dependency', !values.has_dependency)}/>
          </Col>}

          {values.has_dependency && 
            <>
              <Col className='mt-1'>
                <label>{I18n.t('dependent_to')}</label>
                <Select
                  defaultValue={dependencySettings['accessor']}
                  options={columns.map(v => v.cellRendererParams?.original).filter(v => v && v.is_selection).map(v =>({value: v.accessor, label: v.name}))}
                  onChange={(val) => handleDependencySettingsChange('accessor', val)}
                />
              </Col>
              {values.column_type !== 'option_set' && <>
              <Col className='mt-1'>
                <label>{I18n.t('value_formatter')}</label>
                <Select
                  defaultValue={dependencySettings['value_formatter']}
                  options={PRE_DEFINED_VALUE_FORMATTERS.map(v => ({value: v, label: v}))}
                  onChange={(val) => handleDependencySettingsChange('value_formatter', val)}
                />
              </Col>
              <Col className='mt-1'>
                <label className='w-100'>{I18n.t('no_mapping_for_dependency_values')}</label>
                <ToggleSwitch name='no_mapping_defined' isOn={dependencySettings['no_mapping_defined']} defaultIsOn={false} handleToggle={(e) => handleDependencySettingsChange('no_mapping_defined', !dependencySettings['no_mapping_defined'])}/>
              </Col>
              {!dependencySettings['no_mapping_defined'] &&
                <>
                  <Col className='mt-1'>
                    <label className='w-100'>{I18n.t('mapping')}</label>

                    {targetedDependencyOptions.map((option, i) => {
                      return <>
                        <React.Fragment key={`mapping-${i}`}>
                          <Col size='xs-6'>
                            <div className='form-group'>
                              <div className='form-control text-truncate bg-light'>
                                {option.label}
                              </div>
                            </div>
                          </Col>
                          <Col size='xs-6'>
                            <div className='form-group'>
                              <Select
                                options={PRE_DEFINED_VALUE_CONSTRAINTS[dependencySettings['value_formatter']]?.map(v => ({value: v, label: v})) || []}
                                value={((dependencySettings['mapping'] || {})[`option-${option.value}`] || {})['value_constraint'] || ''}
                                onChange={(val) => handleDependencyMappingChange(option.value, val)}
                              />
                            </div>
                          </Col>
                        </React.Fragment>
                      </>
                    })}
                  </Col>
                  </>}
                </>
              }
            </>
          }
        </ColumnTypeDependent>
        {values.is_selection && 
          <ColumnTypeDependent columnType={values.column_type} showOnColumnTypes={['text']}>
            <label>{I18n.t('include_milestone_for_select')}</label>
            <div>  
              <ToggleSwitch name='include_milestone_for_select' isOn={values.include_milestone_for_select} defaultIsOn={false} handleToggle={(e) => handleValueChange('include_milestone_for_select', !values.include_milestone_for_select)} />
            </div>
          </ColumnTypeDependent>
        }

        <ColumnTypeDependent columnType={values.column_type} showOnColumnTypes={['user_choice']}>
          <Col className='mt-1'>
            <label>{I18n.t('use_organisation_tree_picker', {scope: 'fe'})}</label>
            <div>
              <ToggleSwitch
                name='use_organisation_tree_picker'
                isOn={values.additional_settings?.['use_organisation_tree_picker']}
                defaultIsOn={false}
                handleToggle={(e) => handleValueChange('use_organisation_tree_picker', !values.additional_settings?.['use_organisation_tree_picker'], true)}
              />
            </div>
          </Col>
        </ColumnTypeDependent>

        <Col className='mt-1'>
          <label>{I18n.t('datagrid_column_key')}</label>
          <TextInput value={values.key} maxLength='64' onChange={(e) => handleValueChange('key', e.target.value)}/>
        </Col>
      </Row>
  </Dialog>
}


export const ColumnEditPortal = (props) => <Portal component={ColumnEdit}  {...props} />

